import { startOfDay } from 'date-fns'
import { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  useLazyGetRentalFeesByConfiguredServiceQuery,
  useLazyGetRentalFeesResetHistoryQuery,
  useAddRentalFeesToConfiguredServiceMutation,
  useUpdateRentalFeesConfiguredServiceMutation,
  useDeleteRentalFeesFromConfiguredServiceMutation,
} from 'api/configured-service/configuredServiceRentalFeesCrud'
import { useResetGracePeriodMutation, useUndoGracePeriodResetMutation } from 'api/pricing/resetGracePeriod'
import {
  setSelectedDay as setSelectedDayAction,
  setEditModeDismissed as setEditModeDismissedAction,
  setPendingPeriodEdit as setPendingPeriodEditAction,
  selectConfiguredServiceId,
  selectExistingRentalFee,
  selectPendingPeriodEdit,
  selectIsOngoingRentalFee,
  selectIsLatestPeriodClosed,
  selectSelectedDay,
} from 'slices/pricing/rentalFeeDialogSlice'
import T from 'T'
import { createDateFromString, formatDateToBEFormatDateFns } from 'utils/date'
import { handleError } from 'utils/error'
import { formatCentsToDollars, formatDollarsToCents } from 'utils/price'

import {
  generateBillingData,
  getNewPeriodFillEndDate,
  canDateBeNewPeriodStart,
  canRevertGracePeriodReset,
  getRentalFeeResetHistoryIdsForBoundaryDate,
  filterValidRentalPeriods,
  getExistingFeeDatesBE,
  getNewPeriodDateRange,
  buildRentalFeeSaveBody,
  wouldPeriodOverlap,
  hasOverlappingPeriods,
  regenerateBillingDataForEffectivePeriods,
  getBaseBillingData,
  getFinalizedBilledPeriods,
  wouldChangesAffectBilledDays,
  buildDeletePeriodPayload,
} from './rentalPeriodUtils'

/**
 * Provides all rental fee action handlers. Must be called inside FormProvider.
 * Reads state from the Redux slice and form context; no prop drilling needed.
 *
 * @param {Object} [options]
 * @param {Function} [options.onClose] - Callback when editing is complete
 * @param {Function} [options.onSuccessCallback] - Callback after successful save
 */
const useRentalFeeActions = ({ onClose, onSuccessCallback } = {}) => {
  const dispatch = useDispatch()
  const { setValue, getValues, watch } = useFormContext()
  const rentalFee = watch('rentalFee')

  const configuredServiceId = useSelector(selectConfiguredServiceId)
  const existingRentalFee = useSelector(selectExistingRentalFee)
  const pendingPeriodEdit = useSelector(selectPendingPeriodEdit)
  const isOngoingRentalFee = useSelector(selectIsOngoingRentalFee)
  const isLatestPeriodClosed = useSelector(selectIsLatestPeriodClosed)
  const selectedDay = useSelector(selectSelectedDay)

  const [getRentalFeesByConfiguredService] = useLazyGetRentalFeesByConfiguredServiceQuery()
  const [getRentalFeesResetHistory] = useLazyGetRentalFeesResetHistoryQuery()
  const [addRentalFeesToConfiguredService, { isLoading: isAddLoading }] = useAddRentalFeesToConfiguredServiceMutation()
  const [updateRentalFeesConfiguredService, { isLoading: isUpdateLoading }] = useUpdateRentalFeesConfiguredServiceMutation({
    fixedCacheKey: 'rentalFeeUpdate',
  })
  const [deleteRentalFeesFromConfiguredService, { isLoading: isDeleteLoading }] = useDeleteRentalFeesFromConfiguredServiceMutation()
  const [resetGracePeriod, { isLoading: isResetGracePeriodLoading }] = useResetGracePeriodMutation({
    fixedCacheKey: 'rentalFeeResetGracePeriod',
  })
  const [undoGracePeriodReset, { isLoading: isUndoGracePeriodResetLoading }] = useUndoGracePeriodResetMutation({
    fixedCacheKey: 'rentalFeeUndoGracePeriodReset',
  })
  const isSaveLoading = isAddLoading || isUpdateLoading || isDeleteLoading || isResetGracePeriodLoading || isUndoGracePeriodResetLoading

  const isAmountDirty = useMemo(() => {
    if (!existingRentalFee) return false
    return formatDollarsToCents(rentalFee.amount) !== existingRentalFee.amountCents
  }, [existingRentalFee, rentalFee.amount])

  const isActivePeriodDirty = useMemo(() => {
    if (!rentalFee.activePeriodId) return false
    const currentStart = rentalFee.startDate ? formatDateToBEFormatDateFns(rentalFee.startDate) : null
    const currentEnd = rentalFee.endDate ? formatDateToBEFormatDateFns(rentalFee.endDate) : null
    const period = existingRentalFee?.rentalPeriods?.find(p => p.id === rentalFee.activePeriodId)
    if (!period) return Boolean(currentStart || currentEnd)
    return currentStart !== (period.startDate ?? null) || currentEnd !== (period.endDate ?? null)
  }, [rentalFee.activePeriodId, rentalFee.startDate, rentalFee.endDate, existingRentalFee])

  const hasNewPeriodSelection = !rentalFee.activePeriodId && Boolean(rentalFee.startDate || rentalFee.endDate)

  const isSaveDisabled = useMemo(() => {
    if (isSaveLoading) return true
    if (!existingRentalFee) return !rentalFee.rentalFeeId || !rentalFee.startDate
    return !(isAmountDirty || isActivePeriodDirty || hasNewPeriodSelection || Boolean(pendingPeriodEdit))
  }, [
    isSaveLoading,
    existingRentalFee,
    rentalFee.rentalFeeId,
    rentalFee.startDate,
    isAmountDirty,
    isActivePeriodDirty,
    hasNewPeriodSelection,
    pendingPeriodEdit,
  ])

  const hasRentalPeriods = Boolean(existingRentalFee?.rentalPeriods?.length)
  const canAddNewPeriod = isLatestPeriodClosed || !hasRentalPeriods

  const effectiveRentalPeriods = useMemo(() => {
    const periods = existingRentalFee?.rentalPeriods ?? []
    if (!pendingPeriodEdit?.periodId || !periods.length) return periods
    return periods.map(p =>
      p.id === pendingPeriodEdit.periodId
        ? { ...p, startDate: pendingPeriodEdit.startDateBE ?? p.startDate, endDate: pendingPeriodEdit.endDateBE ?? p.endDate }
        : p
    )
  }, [existingRentalFee?.rentalPeriods, pendingPeriodEdit])

  const hasExistingBillingData = useMemo(() => {
    if (!existingRentalFee) return false
    const { billedPeriods = [], unbilledPeriods = [], gracePeriods = [] } = existingRentalFee
    return billedPeriods.length > 0 || unbilledPeriods.length > 0 || gracePeriods.length > 0
  }, [existingRentalFee])

  const isCreatingNewPeriod = useMemo(() => {
    if (!rentalFee.startDate) return false
    if (!hasRentalPeriods) return true
    const currentStartDateStr = formatDateToBEFormatDateFns(rentalFee.startDate)
    return canDateBeNewPeriodStart(currentStartDateStr, effectiveRentalPeriods)
  }, [rentalFee.startDate, hasRentalPeriods, effectiveRentalPeriods])

  const isEndDateDeletable = useMemo(
    () => !hasRentalPeriods || (canAddNewPeriod && rentalFee.startDate && !isOngoingRentalFee),
    [hasRentalPeriods, canAddNewPeriod, rentalFee.startDate, isOngoingRentalFee]
  )

  const canUndoReset = useMemo(() => {
    if (!selectedDay) return false
    const dateStr = formatDateToBEFormatDateFns(new Date(selectedDay))
    return canRevertGracePeriodReset(dateStr, existingRentalFee?.rentalPeriods || [], existingRentalFee?.gracePeriods || [])
  }, [selectedDay, existingRentalFee])

  const fetchRentalFeeData = useCallback(() => {
    if (!configuredServiceId) return
    getRentalFeesByConfiguredService({ id: configuredServiceId }).unwrap().catch(handleError)
    getRentalFeesResetHistory({ id: configuredServiceId }).unwrap().catch(handleError)
  }, [configuredServiceId, getRentalFeesByConfiguredService, getRentalFeesResetHistory])

  const reportSaveSuccess = useCallback(() => {
    toast.success(T.RENTAL_FEES_SAVED_SUCCESSFULLY)
    onSuccessCallback?.()
    onClose?.()
  }, [onSuccessCallback, onClose])

  const handleDaySelect = useCallback(
    day => {
      dispatch(setSelectedDayAction(day ? day.toISOString() : null))
    },
    [dispatch]
  )

  const handleRentalFeeSelect = useCallback(
    selectedRentalFeeItem => {
      if (!selectedRentalFeeItem) return
      dispatch(setPendingPeriodEditAction(null))
      setValue('rentalFee', {
        rentalFeeToConfiguredServiceId: null,
        rentalFeeId: selectedRentalFeeItem.id,
        feeName: selectedRentalFeeItem.feeName,
        pricingPeriod: selectedRentalFeeItem.pricingPeriod,
        gracePeriodDays: selectedRentalFeeItem.gracePeriodDays,
        startDate: null,
        endDate: null,
        amount: formatCentsToDollars(selectedRentalFeeItem.amountCents),
        billingData: { unbilledPeriods: [], billedPeriods: [], gracePeriods: [] },
        rentalPeriods: [],
        activePeriodId: null,
      })
    },
    [setValue, dispatch]
  )

  const handleEnterEditPeriod = useCallback(
    period => {
      if (pendingPeriodEdit?.periodId === period.id) {
        dispatch(setPendingPeriodEditAction(null))
      }
      dispatch(setEditModeDismissedAction(false))
      setValue('rentalFee.activePeriodId', period.id)
      setValue('rentalFee.startDate', period.startDate ? createDateFromString(period.startDate) : null)
      setValue('rentalFee.endDate', period.endDate ? createDateFromString(period.endDate) : null)
    },
    [setValue, dispatch, pendingPeriodEdit?.periodId]
  )

  const rejectIfBilledDaysAffected = useCallback(
    (original, proposed, message) => {
      if (!wouldChangesAffectBilledDays([original], proposed, existingRentalFee?.billedPeriods ?? [])) return false
      // eslint-disable-next-line no-restricted-syntax -- a local validation message, not a failed request; handleError takes an RTKQ error
      toast.error(message)
      return true
    },
    [existingRentalFee?.billedPeriods]
  )

  const handleExitEditMode = useCallback(() => {
    if (rentalFee.activePeriodId && (rentalFee.startDate || rentalFee.endDate)) {
      const startBE = rentalFee.startDate ? formatDateToBEFormatDateFns(rentalFee.startDate) : null
      const endBE = rentalFee.endDate ? formatDateToBEFormatDateFns(rentalFee.endDate) : null
      dispatch(setPendingPeriodEditAction({ periodId: rentalFee.activePeriodId, startDateBE: startBE, endDateBE: endBE }))
    }
    dispatch(setEditModeDismissedAction(true))
    setValue('rentalFee.activePeriodId', null)
    setValue('rentalFee.startDate', null)
    setValue('rentalFee.endDate', null)
  }, [rentalFee.activePeriodId, rentalFee.startDate, rentalFee.endDate, setValue, dispatch])

  const handleRevertEditMode = useCallback(() => {
    if (!rentalFee.activePeriodId || !existingRentalFee?.rentalPeriods?.length) return
    dispatch(setPendingPeriodEditAction(null))
    const period = existingRentalFee.rentalPeriods.find(p => p.id === rentalFee.activePeriodId)
    if (!period) return
    setValue('rentalFee.startDate', period.startDate ? createDateFromString(period.startDate) : null)
    setValue('rentalFee.endDate', period.endDate ? createDateFromString(period.endDate) : null)
    const billingData = regenerateBillingDataForEffectivePeriods(
      existingRentalFee.rentalPeriods,
      rentalFee.activePeriodId,
      period.startDate,
      period.endDate,
      rentalFee.gracePeriodDays,
      existingRentalFee.billedPeriods ?? []
    )
    setValue('rentalFee.billingData', billingData)
  }, [setValue, dispatch, rentalFee.activePeriodId, rentalFee.gracePeriodDays, existingRentalFee])

  const handleEditedPeriodStartChange = useCallback(
    newDate => {
      if (!newDate) {
        setValue('rentalFee.startDate', null)
        return
      }
      const endDate = getValues('rentalFee.endDate')
      const newStartBE = formatDateToBEFormatDateFns(newDate)
      const newEndBE = endDate ? formatDateToBEFormatDateFns(endDate) : null
      if (wouldPeriodOverlap(newStartBE, newEndBE, existingRentalFee?.rentalPeriods ?? [], rentalFee.activePeriodId)) {
        toast.error(T.RENTAL_PERIOD_DATES_CANNOT_OVERLAP)
        return
      }
      const originalPeriod = existingRentalFee?.rentalPeriods?.find(p => p.id === rentalFee.activePeriodId)
      if (
        originalPeriod &&
        rejectIfBilledDaysAffected(originalPeriod, [{ ...originalPeriod, startDate: newStartBE }], T.CANNOT_MODIFY_BILLED_PERIOD_DATES)
      ) {
        return
      }
      setValue('rentalFee.startDate', newDate)
      const newEndAfterStart = endDate && newDate > endDate ? null : endDate
      if (newEndAfterStart !== endDate) {
        setValue('rentalFee.endDate', null)
      }
      const effectiveEndBE = newEndAfterStart ? formatDateToBEFormatDateFns(newEndAfterStart) : null
      const billingData = regenerateBillingDataForEffectivePeriods(
        existingRentalFee?.rentalPeriods ?? [],
        rentalFee.activePeriodId,
        newStartBE,
        effectiveEndBE,
        rentalFee.gracePeriodDays,
        existingRentalFee?.billedPeriods ?? []
      )
      setValue('rentalFee.billingData', billingData)
    },
    [setValue, getValues, rentalFee.activePeriodId, rentalFee.gracePeriodDays, existingRentalFee]
  )

  const handleStartDateChange = useCallback(
    newDate => {
      if (rentalFee.activePeriodId) {
        handleEditedPeriodStartChange(newDate)
        return
      }
      if (!newDate) {
        setValue('rentalFee.startDate', null)
        if (!hasExistingBillingData) {
          setValue('rentalFee.billingData', { unbilledPeriods: [], billedPeriods: [], gracePeriods: [] })
        }
        return
      }
      const newStartBE = formatDateToBEFormatDateFns(newDate)
      if (!canDateBeNewPeriodStart(newStartBE, effectiveRentalPeriods)) {
        toast.error(T.RENTAL_PERIOD_DATES_CANNOT_OVERLAP)
        return
      }
      setValue('rentalFee.startDate', newDate)
      setValue('rentalFee.endDate', null)
      const fillEndDate = getNewPeriodFillEndDate(newDate, effectiveRentalPeriods)
      const generatedBillingData = generateBillingData(newDate, rentalFee.gracePeriodDays, fillEndDate)
      const baseBillingData = getBaseBillingData(pendingPeriodEdit, existingRentalFee, rentalFee.gracePeriodDays)
      setValue('rentalFee.billingData', {
        unbilledPeriods: [...baseBillingData.unbilledPeriods, ...generatedBillingData.unbilledPeriods],
        billedPeriods: baseBillingData.billedPeriods,
        gracePeriods: [...baseBillingData.gracePeriods, ...generatedBillingData.gracePeriods],
      })
    },
    [
      handleEditedPeriodStartChange,
      setValue,
      rentalFee.activePeriodId,
      rentalFee.gracePeriodDays,
      hasExistingBillingData,
      existingRentalFee,
      effectiveRentalPeriods,
      pendingPeriodEdit,
    ]
  )

  const handleEditedPeriodEndChange = useCallback(
    newDate => {
      const startDate = getValues('rentalFee.startDate')
      const newStartBE = startDate ? formatDateToBEFormatDateFns(startDate) : null
      const newEndBE = newDate ? formatDateToBEFormatDateFns(newDate) : null
      if (wouldPeriodOverlap(newStartBE, newEndBE, existingRentalFee?.rentalPeriods ?? [], rentalFee.activePeriodId)) {
        toast.error(T.RENTAL_PERIOD_DATES_CANNOT_OVERLAP)
        return
      }
      const originalPeriod = existingRentalFee?.rentalPeriods?.find(p => p.id === rentalFee.activePeriodId)
      if (
        newDate &&
        originalPeriod &&
        rejectIfBilledDaysAffected(originalPeriod, [{ ...originalPeriod, endDate: newEndBE }], T.CANNOT_MODIFY_BILLED_PERIOD_DATES)
      ) {
        return
      }
      setValue('rentalFee.endDate', newDate)
      const billingData = regenerateBillingDataForEffectivePeriods(
        existingRentalFee?.rentalPeriods ?? [],
        rentalFee.activePeriodId,
        newStartBE,
        newEndBE,
        rentalFee.gracePeriodDays,
        existingRentalFee?.billedPeriods ?? []
      )
      setValue('rentalFee.billingData', billingData)
    },
    [setValue, getValues, rentalFee.activePeriodId, rentalFee.gracePeriodDays, existingRentalFee]
  )

  const handleEndDateChange = useCallback(
    newDate => {
      if (rentalFee.activePeriodId) {
        handleEditedPeriodEndChange(newDate)
        return
      }

      if (isOngoingRentalFee && !rentalFee.startDate && newDate) {
        const ongoingPeriod = existingRentalFee?.rentalPeriods?.find(p => p.startDate && !p.endDate)
        if (ongoingPeriod) {
          const newStartBE = ongoingPeriod.startDate
          const newEndBE = formatDateToBEFormatDateFns(newDate)

          if (wouldPeriodOverlap(newStartBE, newEndBE, existingRentalFee.rentalPeriods, ongoingPeriod.id)) {
            toast.error(T.RENTAL_PERIOD_DATES_CANNOT_OVERLAP)
            return
          }

          if (rejectIfBilledDaysAffected(ongoingPeriod, [{ ...ongoingPeriod, endDate: newEndBE }], T.CANNOT_MODIFY_BILLED_PERIOD_DATES)) {
            return
          }

          setValue('rentalFee.activePeriodId', ongoingPeriod.id)
          setValue('rentalFee.startDate', createDateFromString(ongoingPeriod.startDate))
          setValue('rentalFee.endDate', newDate)
          dispatch(setEditModeDismissedAction(false))

          const billingData = regenerateBillingDataForEffectivePeriods(
            existingRentalFee.rentalPeriods,
            ongoingPeriod.id,
            newStartBE,
            newEndBE,
            rentalFee.gracePeriodDays,
            existingRentalFee.billedPeriods ?? []
          )
          setValue('rentalFee.billingData', billingData)
          return
        }
      }

      if (rentalFee.startDate && newDate) {
        const startBE = formatDateToBEFormatDateFns(rentalFee.startDate)
        const newEndBE = formatDateToBEFormatDateFns(newDate)
        if (wouldPeriodOverlap(startBE, newEndBE, effectiveRentalPeriods)) {
          toast.error(T.RENTAL_PERIOD_DATES_CANNOT_OVERLAP)
          return
        }
      }
      setValue('rentalFee.endDate', newDate)
      const today = startOfDay(new Date())
      const fillUntilDate = newDate ? startOfDay(newDate) : today
      const generatedBillingData = generateBillingData(rentalFee.startDate, rentalFee.gracePeriodDays, fillUntilDate)
      const baseBillingData = getBaseBillingData(pendingPeriodEdit, existingRentalFee, rentalFee.gracePeriodDays)
      setValue('rentalFee.billingData', {
        unbilledPeriods: [...baseBillingData.unbilledPeriods, ...generatedBillingData.unbilledPeriods],
        billedPeriods: baseBillingData.billedPeriods,
        gracePeriods: [...baseBillingData.gracePeriods, ...generatedBillingData.gracePeriods],
      })
    },
    [
      handleEditedPeriodEndChange,
      setValue,
      dispatch,
      rentalFee.activePeriodId,
      rentalFee.startDate,
      rentalFee.gracePeriodDays,
      isOngoingRentalFee,
      existingRentalFee,
      effectiveRentalPeriods,
      pendingPeriodEdit,
    ]
  )

  const handleClearStartDate = useCallback(() => {
    setValue('rentalFee.startDate', null)
    if (rentalFee.endDate) {
      setValue('rentalFee.endDate', null)
    }
    const billingData = getBaseBillingData(pendingPeriodEdit, existingRentalFee, rentalFee.gracePeriodDays)
    setValue('rentalFee.billingData', billingData)
  }, [rentalFee.endDate, rentalFee.gracePeriodDays, setValue, existingRentalFee, pendingPeriodEdit])

  const handleClearEndDate = useCallback(() => {
    setValue('rentalFee.endDate', null)
  }, [setValue])

  const handleResetGracePeriod = useCallback(
    async resetDate => {
      if (!rentalFee.rentalFeeToConfiguredServiceId) {
        toast.error(T.NO_RENTAL_FEES)
        return
      }
      try {
        await resetGracePeriod({
          resetDate: formatDateToBEFormatDateFns(resetDate),
          rentalFeeToConfiguredServiceIds: [rentalFee.rentalFeeToConfiguredServiceId],
        }).unwrap()
        toast.success(T.RESET_GRACE_PERIOD_SUCCESS_MSG)
        fetchRentalFeeData()
      } catch (error) {
        handleError(error)
      }
    },
    [rentalFee.rentalFeeToConfiguredServiceId, resetGracePeriod, fetchRentalFeeData]
  )

  const handleUndoReset = useCallback(
    async resetDate => {
      if (!rentalFee.rentalFeeToConfiguredServiceId) {
        toast.error(T.NO_RENTAL_FEES)
        return
      }
      const dateStr = formatDateToBEFormatDateFns(resetDate)
      const gracePeriods = existingRentalFee?.gracePeriods ?? []
      const rentalFeeResetHistoryIds = getRentalFeeResetHistoryIdsForBoundaryDate(dateStr, gracePeriods)
      if (rentalFeeResetHistoryIds.length === 0) {
        toast.error(T.NO_RESET_HISTORY_FOR_DATE)
        return
      }
      try {
        await undoGracePeriodReset({ rentalFeeResetHistoryIds }).unwrap()
        toast.success(T.UNDO_RESET_SUCCESS_MSG)
        fetchRentalFeeData()
      } catch (error) {
        handleError(error)
        fetchRentalFeeData()
      }
    },
    [rentalFee.rentalFeeToConfiguredServiceId, existingRentalFee?.gracePeriods, undoGracePeriodReset, fetchRentalFeeData]
  )

  const handleDeleteRentalPeriod = useCallback(
    async periodId => {
      if (!periodId || !existingRentalFee?.rentalPeriods?.length) return

      const periodToDelete = existingRentalFee.rentalPeriods.find(p => p.id === periodId)
      if (!periodToDelete) return

      if (rejectIfBilledDaysAffected(periodToDelete, [], T.CANNOT_DELETE_PERIOD_WITH_FINALIZED_CHARGES)) {
        return
      }

      try {
        const isLastPeriod = existingRentalFee.rentalPeriods.length === 1

        if (isLastPeriod) {
          await deleteRentalFeesFromConfiguredService({
            id: configuredServiceId,
            rentalFeeToConfiguredServiceIds: [rentalFee.rentalFeeToConfiguredServiceId],
          }).unwrap()
        } else {
          const body = buildDeletePeriodPayload(existingRentalFee.rentalPeriods, periodId)

          await updateRentalFeesConfiguredService({
            id: configuredServiceId,
            rentalFeesToConfiguredService: [
              {
                rentalFeeToConfiguredServiceId: rentalFee.rentalFeeToConfiguredServiceId,
                rentalFeeId: rentalFee.rentalFeeId,
                ...body,
              },
            ],
          }).unwrap()
        }

        if (pendingPeriodEdit?.periodId === periodId) {
          dispatch(setPendingPeriodEditAction(null))
        }
        if (rentalFee.activePeriodId === periodId) {
          setValue('rentalFee.activePeriodId', null)
          setValue('rentalFee.startDate', null)
          setValue('rentalFee.endDate', null)
        }

        toast.success(T.RENTAL_FEES_SAVED_SUCCESSFULLY)
        fetchRentalFeeData()
      } catch (error) {
        handleError(error)
        fetchRentalFeeData()
      }
    },
    [
      configuredServiceId,
      existingRentalFee,
      rentalFee.rentalFeeToConfiguredServiceId,
      rentalFee.rentalFeeId,
      rentalFee.activePeriodId,
      pendingPeriodEdit?.periodId,
      updateRentalFeesConfiguredService,
      deleteRentalFeesFromConfiguredService,
      fetchRentalFeeData,
      dispatch,
      setValue,
    ]
  )

  const handleRemoveRentalFee = useCallback(async () => {
    if (existingRentalFee) {
      if (getFinalizedBilledPeriods(existingRentalFee.billedPeriods).length > 0) {
        toast.error(T.CANNOT_DELETE_RENTAL_FEE_WITH_BILLED_CHARGES)
        return
      }
      try {
        await deleteRentalFeesFromConfiguredService({
          id: configuredServiceId,
          rentalFeeToConfiguredServiceIds: [existingRentalFee.id],
        }).unwrap()
        reportSaveSuccess()
      } catch (error) {
        handleError(error)
        fetchRentalFeeData()
        return
      }
    }
    onClose?.()
  }, [configuredServiceId, existingRentalFee, deleteRentalFeesFromConfiguredService, reportSaveSuccess, fetchRentalFeeData, onClose])

  const handleSave = useCallback(
    async data => {
      const { rentalFee: currentFee } = data

      if (!currentFee.rentalFeeId) {
        await handleRemoveRentalFee()
        return
      }

      try {
        const formattedStart = currentFee.startDate ? formatDateToBEFormatDateFns(currentFee.startDate) : null
        const formattedEnd = currentFee.endDate ? formatDateToBEFormatDateFns(currentFee.endDate) : null

        const existingPeriods = existingRentalFee?.rentalPeriods || []
        let validPeriods = filterValidRentalPeriods(existingPeriods)
        if (validPeriods.length < existingPeriods.length) {
          toast.warning(T.RENTAL_PERIOD_INVALID_DATE_RANGES_REMOVED)
        }

        if (pendingPeriodEdit) {
          validPeriods = validPeriods.map(p =>
            p.id === pendingPeriodEdit.periodId
              ? { ...p, startDate: pendingPeriodEdit.startDateBE, endDate: pendingPeriodEdit.endDateBE }
              : p
          )
        }

        if (
          currentFee.activePeriodId &&
          formattedStart &&
          wouldPeriodOverlap(formattedStart, formattedEnd, validPeriods, currentFee.activePeriodId)
        ) {
          toast.error(T.RENTAL_PERIOD_DATES_CANNOT_OVERLAP)
          return
        }

        const { existingFeeStartBE, existingFeeEndBE } = getExistingFeeDatesBE(existingRentalFee)
        const { newPeriodStart, newPeriodEnd } = getNewPeriodDateRange({
          currentFee,
          validPeriods,
          formattedStart,
          formattedEnd,
          existingFeeStartBE,
          existingFeeEndBE,
          existingRentalFee,
          isCreatingNewPeriod,
        })
        const activePeriodIdForPayload = pendingPeriodEdit ? null : currentFee.activePeriodId
        const body = buildRentalFeeSaveBody(validPeriods, activePeriodIdForPayload, newPeriodStart, newPeriodEnd)
        if (hasOverlappingPeriods(body.rentalPeriods)) {
          toast.error(T.RENTAL_PERIOD_DATES_CANNOT_OVERLAP)
          return
        }

        const billedPeriods = existingRentalFee?.billedPeriods ?? []
        if (billedPeriods.length > 0) {
          const originalPeriods = existingRentalFee?.rentalPeriods ?? []
          if (wouldChangesAffectBilledDays(originalPeriods, body.rentalPeriods, billedPeriods)) {
            toast.error(T.CANNOT_MODIFY_BILLED_PERIOD_DATES)
            return
          }
        }

        const amountCents = formatDollarsToCents(currentFee.amount)
        if (!Number.isFinite(amountCents) || amountCents <= 0) {
          toast.error(T.AMOUNT_MUST_BE_GREATER_THAN_ZERO)
          return
        }

        if (!currentFee.rentalFeeToConfiguredServiceId) {
          await addRentalFeesToConfiguredService({
            id: configuredServiceId,
            configuredRentalFees: [{ rentalFeeId: currentFee.rentalFeeId, amountCents, ...body }],
          }).unwrap()
        } else {
          await updateRentalFeesConfiguredService({
            id: configuredServiceId,
            rentalFeesToConfiguredService: [
              {
                rentalFeeToConfiguredServiceId: currentFee.rentalFeeToConfiguredServiceId,
                rentalFeeId: currentFee.rentalFeeId,
                amountCents,
                ...body,
              },
            ],
          }).unwrap()
        }

        setValue('rentalFee.startDate', null)
        setValue('rentalFee.endDate', null)
        setValue('rentalFee.activePeriodId', null)
        dispatch(setPendingPeriodEditAction(null))
        reportSaveSuccess()
      } catch (error) {
        handleError(error)
        fetchRentalFeeData()
      }
    },
    [
      configuredServiceId,
      existingRentalFee,
      isCreatingNewPeriod,
      pendingPeriodEdit,
      handleRemoveRentalFee,
      addRentalFeesToConfiguredService,
      updateRentalFeesConfiguredService,
      fetchRentalFeeData,
      reportSaveSuccess,
      setValue,
      dispatch,
    ]
  )

  return {
    handleDaySelect,
    handleRentalFeeSelect,
    handleEnterEditPeriod,
    handleExitEditMode,
    handleRevertEditMode,
    handleStartDateChange,
    handleEndDateChange,
    handleClearStartDate,
    handleClearEndDate,
    handleResetGracePeriod,
    handleUndoReset,
    handleDeleteRentalPeriod,
    handleSave,
    fetchRentalFeeData,
    isCreatingNewPeriod,
    isEndDateDeletable,
    canUndoReset,
    isSaveLoading,
    isSaveDisabled,
  }
}

export default useRentalFeeActions
